import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import { api, formatApiError } from "@/lib/api";
import { t, LANGS, itemText, sectionTitle, fmtDateTime } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, X, Zap, ArrowRight, ClipboardCheck, DoorOpen, Sparkles } from "lucide-react";

export default function SupervisorChecklist() {
  const { user } = useAuth();
  const { property } = useProperty();
  const nav = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState("");
  const [shift, setShift] = useState("");
  const [supName, setSupName] = useState(user?.name || "");
  const [serviceType, setServiceType] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestCount, setGuestCount] = useState("");
  const [language, setLanguage] = useState("en");
  const [remarks, setRemarks] = useState("");
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!property) return;
    api.get("/config/rooms", { params: { property_id: property.id } })
      .then((r) => {
        setRooms(r.data.rooms);
        setShifts(r.data.shifts);
        setShift(user?.shift || r.data.shifts[0]);
      })
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  }, [user, property]);

  // Checklist (base + custom points) reloads when room or cleaning type changes —
  // service rooms skip checkout-only items (e.g. WC band)
  useEffect(() => {
    if (!property) return;
    api.get("/config/checklist", { params: { property_id: property.id, room: selectedRoom || undefined, service_type: serviceType || undefined } })
      .then((c) => {
        setSections(c.data.sections);
        const valid = new Set(c.data.sections.flatMap((s) => s.items.map((it) => it.id)));
        setAnswers((a) => Object.fromEntries(Object.entries(a).filter(([id]) => valid.has(id))));
      })
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  }, [property, selectedRoom, serviceType]);

  const totalItems = useMemo(() => sections.reduce((n, s) => n + s.items.length, 0), [sections]);
  const answered = Object.keys(answers).length;
  const hasNo = useMemo(() => Object.values(answers).some((v) => v === false), [answers]);
  const optionalIds = useMemo(
    () => new Set(sections.flatMap((s) => s.items).filter((it) => it.optional).map((it) => it.id)),
    [sections]
  );
  const hasMandatoryNo = useMemo(
    () => Object.entries(answers).some(([id, v]) => v === false && !optionalIds.has(id)),
    [answers, optionalIds]
  );

  const setAnswer = (id, val) => setAnswers((a) => ({ ...a, [id]: val }));

  const markSectionYes = (sec) => {
    setAnswers((a) => {
      const next = { ...a };
      sec.items.forEach((it) => (next[it.id] = true));
      return next;
    });
    toast(`${sectionTitle(sec, language)} — ${t(language, "all_yes")}`);
  };

  const canSubmit =
    selectedRoom && shift && supName.trim() && serviceType &&
    guestName.trim() && Number(guestCount) >= 1 &&
    answered === totalItems && totalItems > 0 &&
    (!hasMandatoryNo || remarks.trim()) && !submitting;

  const onSubmit = async () => {
    if (!canSubmit) {
      if (!selectedRoom) toast.error(t(language, "select_room"));
      else if (!serviceType) toast.error("Select cleaning type — Check-out Room or Service Room");
      else if (!guestName.trim()) toast.error("Guest name is required");
      else if (!(Number(guestCount) >= 1)) toast.error("Number of guests is required");
      else if (answered < totalItems) toast.error(`All items compulsory — ${answered}/${totalItems} answered`);
      else if (hasMandatoryNo && !remarks.trim()) toast.error("Remarks are compulsory — one or more items marked No");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        property_id: property.id,
        room_number: selectedRoom,
        shift,
        supervisor_name: supName,
        service_type: serviceType,
        guest_name: guestName,
        guest_count: Number(guestCount),
        language,
        remarks,
        answers: Object.entries(answers).map(([item_id, answer]) => ({ item_id, answer, note: null })),
      };
      const { data } = await api.post("/inspections", payload);
      setResult(data);
      setResultOpen(true);
      toast.success(`${t(language, "checklist_submitted")} · Room ${data.room_number}`);
      if (data.no_count > 0) toast.warning(`${data.no_count} item(s) marked No — owner has been notified`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForNext = () => {
    setAnswers({});
    setSelectedRoom("");
    setServiceType("");
    setGuestName("");
    setGuestCount("");
    setRemarks("");
    setResultOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const floorGroups = useMemo(() => {
    const g = {};
    rooms.forEach((r) => {
      if (!g[r.floor]) g[r.floor] = [];
      g[r.floor].push(r);
    });
    return Object.entries(g).sort(([a], [b]) => Number(a) - Number(b));
  }, [rooms]);

  if (!property) {
    return (
      <Layout title="No Property Assigned" subtitle="Housekeeping Checklist">
        <div className="bg-white border border-[var(--border-warm)] rounded-lg p-8 text-center text-[var(--ink-2)]">
          Your account is not linked to a property. Contact your admin.
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Room Cleaning Checklist" subtitle={`${property.name} · ${t(language, "language")}: ${LANGS.find((l) => l.id === language)?.label}`}>
      {/* Language + note strip */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 no-print">
        <div className="flex items-center gap-2">
          <span className="hotel-overline mr-1">{t(language, "language")}</span>
          {LANGS.map((l) => (
            <button
              key={l.id}
              type="button"
              data-testid={`language-toggle-${l.id}`}
              onClick={() => setLanguage(l.id)}
              className={`sop-pill px-4 h-10 rounded-md border-2 text-sm font-semibold ${
                language === l.id
                  ? "bg-[var(--navy)] border-[var(--navy)] text-white"
                  : "bg-white border-[var(--border-warm)] text-[var(--ink-2)] hover:border-[var(--navy)]"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="text-xs text-[var(--muted)] italic">{t(language, "cleaning_note")}</div>
      </div>

      {/* Setup card */}
      <div className="bg-white border border-[var(--border-warm)] rounded-lg p-6 mb-6 fade-up shadow-sm">
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">{t(language, "room")} *</Label>
            <Select value={selectedRoom} onValueChange={setSelectedRoom}>
              <SelectTrigger data-testid="room-select-dropdown" className="mt-2 h-12 bg-white border-[var(--border-warm)]">
                <SelectValue placeholder={t(language, "select_room")} />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {floorGroups.map(([floor, list]) => (
                  <div key={floor}>
                    <div className="px-2 py-1 hotel-overline">Floor {floor}</div>
                    {list.map((r) => (
                      <SelectItem key={r.number} value={r.number} data-testid={`room-option-${r.number}`}>
                        <span className="font-mono-hotel font-semibold mr-2">{r.number}</span>
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">{t(language, "shift")} *</Label>
            <Select value={shift} onValueChange={setShift}>
              <SelectTrigger data-testid="shift-select-dropdown" className="mt-2 h-12 bg-white border-[var(--border-warm)]">
                <SelectValue placeholder={t(language, "select_shift")} />
              </SelectTrigger>
              <SelectContent>
                {shifts.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">{t(language, "inspector_name")} *</Label>
            <Input
              data-testid="supervisor-name-input"
              value={supName}
              onChange={(e) => setSupName(e.target.value)}
              className="mt-2 h-12 bg-white border-[var(--border-warm)]"
            />
          </div>
        </div>

        {/* Cleaning type + guest details */}
        <div className="grid md:grid-cols-3 gap-6 mt-6 pt-6 border-t border-[var(--border-subtle)]">
          <div>
            <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">{t(language, "service_type")} *</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                type="button"
                data-testid="service-type-checkout"
                onClick={() => setServiceType("checkout")}
                className={`sop-pill h-12 rounded-md border-2 text-sm font-semibold flex items-center justify-center gap-2 ${
                  serviceType === "checkout"
                    ? "bg-[var(--navy)] border-[var(--navy)] text-white"
                    : "bg-white border-[var(--border-warm)] text-[var(--ink-2)] hover:border-[var(--navy)]"
                }`}
              >
                <DoorOpen className="w-4 h-4" /> {t(language, "checkout_room")}
              </button>
              <button
                type="button"
                data-testid="service-type-service"
                onClick={() => setServiceType("service")}
                className={`sop-pill h-12 rounded-md border-2 text-sm font-semibold flex items-center justify-center gap-2 ${
                  serviceType === "service"
                    ? "bg-[var(--navy)] border-[var(--navy)] text-white"
                    : "bg-white border-[var(--border-warm)] text-[var(--ink-2)] hover:border-[var(--navy)]"
                }`}
              >
                <Sparkles className="w-4 h-4" /> {t(language, "service_room")}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">{t(language, "guest_name")} *</Label>
            <Input
              data-testid="guest-name-input"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="e.g. Aarav Mehta"
              className="mt-2 h-12 bg-white border-[var(--border-warm)]"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">{t(language, "no_of_guests")} *</Label>
            <Input
              data-testid="guest-count-input"
              type="number"
              min="1"
              value={guestCount}
              onChange={(e) => setGuestCount(e.target.value)}
              placeholder="e.g. 2"
              className="mt-2 h-12 bg-white border-[var(--border-warm)]"
            />
          </div>
        </div>
      </div>

      {/* Sticky progress HUD */}
      <div className="sticky top-[85px] z-20 mb-6 no-print">
        <div className="navy-card rounded-lg p-4 flex items-center justify-between gap-4 shadow-lg">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-md bg-white/10 border border-[var(--gold)]/40 text-[var(--gold)] flex items-center justify-center">
              <ClipboardCheck className="w-6 h-6" />
            </div>
            <div>
              <div className="hotel-overline text-[var(--gold)]">{t(language, "progress")}</div>
              <div className="font-display text-2xl" data-testid="answered-count">
                {answered}/{totalItems} <span className="text-sm text-white/60 font-sans">{t(language, "answered")}</span>
              </div>
              <div className="text-xs text-white/60 font-mono-hotel">
                {selectedRoom ? `Room ${selectedRoom}` : "—"}{serviceType ? ` · ${serviceType === "checkout" ? t(language, "checkout_room") : t(language, "service_room")}` : ""}
              </div>
            </div>
          </div>
          <Button
            data-testid="submit-inspection-button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="h-12 px-6 bg-[var(--gold)] hover:bg-[var(--gold-hover)] text-[var(--navy)] font-semibold rounded-md disabled:opacity-50"
          >
            {t(language, "submit_checklist")} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
        <div className="h-1 bg-[var(--border-subtle)] rounded-b-lg overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[var(--gold)] to-[var(--gold-hover)] transition-all duration-500"
            style={{ width: `${(answered / (totalItems || 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-6">
        {sections.map((sec, idx) => {
          const secYes = sec.items.filter((it) => answers[it.id] === true).length;
          return (
            <div
              key={sec.id}
              data-testid={`checklist-section-${sec.id}`}
              className="bg-white border border-[var(--border-warm)] rounded-lg overflow-hidden fade-up-2"
            >
              <div className="flex items-start justify-between gap-4 p-5 border-b border-[var(--border-subtle)] bg-[var(--surface-elev)]">
                <div>
                  <div className="hotel-overline">Section {idx + 1} · {sec.items.length} items</div>
                  <h3 className="font-display text-2xl text-[var(--navy)] mt-1">{sectionTitle(sec, language)}</h3>
                  <p className="text-sm text-[var(--ink-2)] mt-1">{sec.description}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <div className="hotel-overline">Done</div>
                    <div className="font-mono-hotel text-lg text-[var(--navy)]">{secYes}/{sec.items.length}</div>
                  </div>
                  {user?.allow_all_yes !== false && (
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`mark-all-yes-section-${sec.id}`}
                      onClick={() => markSectionYes(sec)}
                      className="border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold-light)]"
                    >
                      <Zap className="w-3.5 h-3.5 mr-1" /> {t(language, "all_yes")}
                    </Button>
                  )}
                </div>
              </div>
              <ul className="divide-y divide-[var(--border-subtle)]">
                {sec.items.map((it, i) => {
                  const val = answers[it.id];
                  return (
                    <li key={it.id} className="p-5 flex items-start gap-4 hover:bg-[var(--surface-elev)]/60 transition-colors">
                      <div className="w-9 h-9 rounded-md bg-[var(--gold-light)] text-[var(--gold)] flex items-center justify-center shrink-0 font-mono-hotel text-xs font-semibold">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] text-[var(--ink)] leading-relaxed">{itemText(it, language)}</p>
                        <div className="text-xs text-[var(--muted)] font-mono-hotel mt-1">
                          {it.code}{it.room_number ? ` · Room ${it.room_number}` : it.code === "ADDL" ? " · Property-wide" : ""}
                        </div>
                        {it.optional && (
                          <span data-testid={`item-optional-tag-${it.id}`} className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-[var(--surface-elev)] border border-[var(--border-warm)] text-[var(--ink-2)]">
                            {t(language, "optional_tag")}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          type="button"
                          data-testid={`item-yes-toggle-${it.id}`}
                          onClick={() => setAnswer(it.id, true)}
                          className={`sop-pill w-16 h-11 rounded-md border-2 font-semibold text-sm flex items-center justify-center gap-1 ${
                            val === true
                              ? "bg-[var(--success-bg)] border-[var(--success)] text-[var(--success)]"
                              : "bg-white border-[var(--border-warm)] text-[var(--ink-2)] hover:border-[var(--success)]"
                          }`}
                        >
                          <Check className="w-4 h-4" /> {t(language, "yes")}
                        </button>
                        <button
                          type="button"
                          data-testid={`item-no-toggle-${it.id}`}
                          onClick={() => setAnswer(it.id, false)}
                          className={`sop-pill w-16 h-11 rounded-md border-2 font-semibold text-sm flex items-center justify-center gap-1 ${
                            val === false
                              ? "bg-[var(--danger-bg)] border-[var(--danger)] text-[var(--danger)]"
                              : "bg-white border-[var(--border-warm)] text-[var(--ink-2)] hover:border-[var(--danger)]"
                          }`}
                        >
                          <X className="w-4 h-4" /> {t(language, "no")}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Remarks */}
      <div className="mt-6 bg-white border border-[var(--border-warm)] rounded-lg p-6">
        <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">
          {t(language, "remarks")}{hasMandatoryNo && <span data-testid="remarks-compulsory-hint" className="text-[var(--danger)] normal-case tracking-normal"> * compulsory — item(s) marked No</span>}
        </Label>
        <Textarea
          data-testid="supervisor-remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          className={`mt-2 bg-white min-h-[100px] ${hasMandatoryNo && !remarks.trim() ? "border-[var(--danger)]" : "border-[var(--border-warm)]"}`}
        />
      </div>

      {/* Success modal */}
      <Dialog open={resultOpen} onOpenChange={setResultOpen}>
        <DialogContent data-testid="inspection-success-modal" className="max-w-md">
          <DialogHeader>
            <div className="w-14 h-14 rounded-full bg-[var(--gold-light)] flex items-center justify-center mx-auto mb-2">
              <ClipboardCheck className="w-8 h-8 text-[var(--gold)]" />
            </div>
            <DialogTitle className="font-display text-3xl text-center text-[var(--navy)]">
              {t(language, "checklist_submitted")}
            </DialogTitle>
            <DialogDescription className="text-center">
              {property.name} · Room <span className="font-mono-hotel font-semibold">{result?.room_number}</span>
            </DialogDescription>
          </DialogHeader>
          {result && (
            <div className="text-center py-2 space-y-2">
              <span className="inline-block px-3 py-1 rounded-full text-xs uppercase tracking-wider font-semibold bg-[var(--navy)] text-white">
                {result.service_type === "checkout" ? t(language, "checkout_room") : t(language, "service_room")}
              </span>
              <div className="text-sm text-[var(--ink)]">
                {result.guest_name} · {result.guest_count} guest{result.guest_count > 1 ? "s" : ""}
              </div>
              <div data-testid="submitted-at-display" className="font-mono-hotel text-sm text-[var(--ink-2)]">
                {t(language, "submitted_at")}: {fmtDateTime(result.created_at)}
              </div>
              <div className="text-xs text-[var(--muted)]">{result.answered_count}/{result.total_items} {t(language, "answered")}</div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => nav(`/inspections/${result?.id}`)} className="border-[var(--border-warm)]">
              {t(language, "view_report")}
            </Button>
            <Button data-testid="clean-next-room-button" onClick={resetForNext} className="bg-[var(--navy)] hover:bg-[var(--navy-2)] text-white">
              {t(language, "clean_next_room")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
